/**
 * Proposal Service - Document Generation Domain
 *
 * AI-powered proposal generation and lifecycle management.
 * Follows Phase 4 architecture with ServiceRegistry integration.
 *
 * Key Flows:
 * 1. Sales rep provides context → AI generates proposal sections + pricing
 * 2. Manual creation with template selection
 * 3. Proposal → accepted → auto-generate invoice (mycelium event)
 *
 * @module services/document-generation/proposal.service
 * @since 2026-02-20
 */
import { supabase } from '../../integrations/supabase/client';
import { createServiceError, type ServiceResponse, type IService, } from '../service-registry';
import { logger } from '../../lib/logger';
import type { Proposal, ProposalStatus, GenerateProposalRequest, UpdateProposalRequest, DocumentGenerationFilters, ProposalAnalytics, DocumentTemplate, } from '@flowstate/types';
// ============================================================================
// SERVICE INTERFACE
// ============================================================================
export interface IProposalService extends IService {
    generateProposal(userId: string, request: GenerateProposalRequest): Promise<ServiceResponse<Proposal>>;
    getProposal(proposalId: string): Promise<ServiceResponse<Proposal>>;
    listProposals(userId: string, filters?: DocumentGenerationFilters): Promise<ServiceResponse<Proposal[]>>;
    updateProposal(proposalId: string, request: UpdateProposalRequest): Promise<ServiceResponse<Proposal>>;
    updateProposalStatus(proposalId: string, status: ProposalStatus, reason?: string): Promise<ServiceResponse<Proposal>>;
    deleteProposal(proposalId: string): Promise<ServiceResponse<void>>;
    getProposalAnalytics(teamId?: string): Promise<ServiceResponse<ProposalAnalytics>>;
    listTemplates(templateType: 'proposal'): Promise<ServiceResponse<DocumentTemplate[]>>;
    duplicateProposal(proposalId: string, userId: string): Promise<ServiceResponse<Proposal>>;
}
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export class ProposalService implements IProposalService {
    readonly name = 'proposal';
    async initialize(): Promise<void> {
        logger.info('📄 Proposal service initialized');
    }
    async dispose(): Promise<void> {
        logger.info('📄 Proposal service disposed');
    }
    // --------------------------------------------------------------------------
    // GENERATE PROPOSAL (with optional AI)
    // --------------------------------------------------------------------------
    async generateProposal(userId: string, request: GenerateProposalRequest): Promise<ServiceResponse<Proposal>> {
        try {
            logger.info('📄 Generating proposal', { userId, hasLineItems: !!request.line_items?.length });
            // Generate proposal number
            const { data: numberData, error: numberError } = await supabase
                .rpc('generate_proposal_number');
            if (numberError) {
                return { success: false, error: numberError.message, code: 'PROPOSAL_NUMBER_FAILED', service: this.name };
            }
            const proposalNumber = numberData as string;
            // Build line items with totals
            const lineItems = (request.line_items || []).map((item, index) => {
                const discount = item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0;
                const subtotal = item.quantity * item.unit_price - discount;
                const tax = item.tax_rate ? subtotal * (item.tax_rate / 100) : 0;
                return {
                    id: crypto.randomUUID(),
                    description: item.description,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    discount_percent: item.discount_percent || 0,
                    tax_rate: item.tax_rate || 0,
                    total: subtotal + tax,
                    sort_order: index,
                };
            });
            // Calculate totals
            const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
            const discountAmount = lineItems.reduce((sum, item) => {
                return sum + (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
            }, 0);
            const taxAmount = lineItems.reduce((sum, item) => {
                const itemSubtotal = item.quantity * item.unit_price - (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                return sum + (item.tax_rate ? itemSubtotal * (item.tax_rate / 100) : 0);
            }, 0);
            const totalAmount = subtotal - discountAmount + taxAmount;
            // Calculate validity dates
            const validDays = request.valid_days || 30;
            const validUntil = new Date();
            validUntil.setDate(validUntil.getDate() + validDays);
            // Default sections for manual proposals
            const sections = [
                {
                    id: crypto.randomUUID(),
                    title: 'Introduction',
                    content: request.project_description || '',
                    sort_order: 0,
                    section_type: 'introduction' as const,
                },
                {
                    id: crypto.randomUUID(),
                    title: 'Scope of Work',
                    content: request.scope_of_work || '',
                    sort_order: 1,
                    section_type: 'scope' as const,
                },
                {
                    id: crypto.randomUUID(),
                    title: 'Pricing',
                    content: '',
                    sort_order: 2,
                    section_type: 'pricing' as const,
                },
                {
                    id: crypto.randomUUID(),
                    title: 'Terms & Conditions',
                    content: '',
                    sort_order: 3,
                    section_type: 'terms' as const,
                },
            ];
            const proposalData = {
                title: `Proposal for ${request.client_name || 'Client'}`,
                proposal_number: proposalNumber,
                status: 'draft' as ProposalStatus,
                client_id: request.client_id || null,
                opportunity_id: request.opportunity_id || null,
                created_by: userId,
                team_id: request.team_id || null,
                sections: JSON.stringify(sections),
                line_items: JSON.stringify(lineItems),
                subtotal,
                tax_amount: taxAmount,
                discount_amount: discountAmount,
                total_amount: totalAmount,
                currency: request.currency || 'AUD',
                valid_from: new Date().toISOString(),
                valid_until: validUntil.toISOString(),
                ai_generated: false,
                template_id: request.template_id || null,
                version: 1,
                metadata: JSON.stringify({
                    client_industry: request.client_industry,
                    budget_range: request.budget_range,
                    timeline: request.timeline,
                    special_requirements: request.special_requirements,
                }),
            };
            const { data, error } = await supabase
                .from('proposals')
                .insert(proposalData)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_CREATE_FAILED', service: this.name };
            }
            const proposal = this.mapProposal(data);
            logger.info('✅ Proposal created', { proposalId: proposal.id, number: proposal.proposal_number });
            return { success: true, data: proposal };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_GENERATE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // GET PROPOSAL
    // --------------------------------------------------------------------------
    async getProposal(proposalId: string): Promise<ServiceResponse<Proposal>> {
        try {
            const { data, error } = await supabase
                .from('proposals')
                .select('*')
                .eq('id', proposalId)
                .single();
            if (error || !data) {
                return { success: false, error: error?.message || 'Proposal not found', code: 'PROPOSAL_NOT_FOUND', service: this.name };
            }
            return { success: true, data: this.mapProposal(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // LIST PROPOSALS
    // --------------------------------------------------------------------------
    async listProposals(userId: string, filters?: DocumentGenerationFilters): Promise<ServiceResponse<Proposal[]>> {
        try {
            let query = supabase
                .from('proposals')
                .select('*')
                .order('created_at', { ascending: false });
            // Apply filters
            if (filters?.client_id)
                query = query.eq('client_id', filters.client_id);
            if (filters?.opportunity_id)
                query = query.eq('opportunity_id', filters.opportunity_id);
            if (filters?.team_id)
                query = query.eq('team_id', filters.team_id);
            if (filters?.status?.length)
                query = query.in('status', filters.status);
            if (filters?.date_from)
                query = query.gte('created_at', filters.date_from);
            if (filters?.date_to)
                query = query.lte('created_at', filters.date_to);
            if (filters?.amount_min)
                query = query.gte('total_amount', filters.amount_min);
            if (filters?.amount_max)
                query = query.lte('total_amount', filters.amount_max);
            if (filters?.ai_generated !== undefined)
                query = query.eq('ai_generated', filters.ai_generated);
            if (filters?.search)
                query = query.ilike('title', `%${filters.search}%`);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSALS_FETCH_FAILED', service: this.name };
            }
            const proposals = (data || []).map(row => this.mapProposal(row));
            return { success: true, data: proposals };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSALS_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // UPDATE PROPOSAL
    // --------------------------------------------------------------------------
    async updateProposal(proposalId: string, request: UpdateProposalRequest): Promise<ServiceResponse<Proposal>> {
        try {
            const updateData: Record<string, unknown> = {};
            if (request.title !== undefined)
                updateData.title = request.title;
            if (request.status !== undefined)
                updateData.status = request.status;
            if (request.sections !== undefined)
                updateData.sections = JSON.stringify(request.sections);
            if (request.valid_until !== undefined)
                updateData.valid_until = request.valid_until;
            if (request.notes !== undefined)
                updateData.notes = request.notes;
            if (request.rejection_reason !== undefined)
                updateData.rejection_reason = request.rejection_reason;
            if (request.metadata !== undefined)
                updateData.metadata = JSON.stringify(request.metadata);
            if (request.line_items !== undefined) {
                updateData.line_items = JSON.stringify(request.line_items);
                // Recalculate totals
                const subtotal = request.line_items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
                const discountAmount = request.line_items.reduce((sum, item) => {
                    return sum + (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                }, 0);
                const taxAmount = request.line_items.reduce((sum, item) => {
                    const itemSubtotal = item.quantity * item.unit_price - (item.discount_percent ? item.quantity * item.unit_price * (item.discount_percent / 100) : 0);
                    return sum + (item.tax_rate ? itemSubtotal * (item.tax_rate / 100) : 0);
                }, 0);
                updateData.subtotal = subtotal;
                updateData.discount_amount = discountAmount;
                updateData.tax_amount = taxAmount;
                updateData.total_amount = subtotal - discountAmount + taxAmount;
            }
            const { data, error } = await supabase
                .from('proposals')
                .update(updateData)
                .eq('id', proposalId)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_UPDATE_FAILED', service: this.name };
            }
            return { success: true, data: this.mapProposal(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_UPDATE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // UPDATE PROPOSAL STATUS
    // --------------------------------------------------------------------------
    async updateProposalStatus(proposalId: string, status: ProposalStatus, reason?: string): Promise<ServiceResponse<Proposal>> {
        try {
            const updateData: Record<string, unknown> = { status };
            // Set tracking timestamps
            switch (status) {
                case 'sent':
                    updateData.sent_at = new Date().toISOString();
                    break;
                case 'viewed':
                    updateData.viewed_at = new Date().toISOString();
                    break;
                case 'accepted':
                    updateData.accepted_at = new Date().toISOString();
                    break;
                case 'rejected':
                    updateData.rejected_at = new Date().toISOString();
                    if (reason)
                        updateData.rejection_reason = reason;
                    break;
            }
            const { data, error } = await supabase
                .from('proposals')
                .update(updateData)
                .eq('id', proposalId)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_STATUS_UPDATE_FAILED', service: this.name };
            }
            logger.info('📄 Proposal status updated', { proposalId, status });
            return { success: true, data: this.mapProposal(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_STATUS_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // DELETE PROPOSAL
    // --------------------------------------------------------------------------
    async deleteProposal(proposalId: string): Promise<ServiceResponse<void>> {
        try {
            const { error } = await supabase
                .from('proposals')
                .delete()
                .eq('id', proposalId);
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_DELETE_FAILED', service: this.name };
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_DELETE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // ANALYTICS
    // --------------------------------------------------------------------------
    async getProposalAnalytics(teamId?: string): Promise<ServiceResponse<ProposalAnalytics>> {
        try {
            let query = supabase.from('proposals').select('*');
            if (teamId)
                query = query.eq('team_id', teamId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_ANALYTICS_FAILED', service: this.name };
            }
            const proposals = data || [];
            const statusCounts = {} as Record<ProposalStatus, number>;
            const statuses: ProposalStatus[] = ['draft', 'ai_generating', 'review', 'sent', 'viewed', 'accepted', 'rejected', 'expired', 'revised'];
            statuses.forEach(s => { statusCounts[s] = proposals.filter(p => p.status === s).length; });
            const accepted = proposals.filter(p => p.status === 'accepted');
            const rejected = proposals.filter(p => p.status === 'rejected');
            const decided = accepted.length + rejected.length;
            const analytics: ProposalAnalytics = {
                total_proposals: proposals.length,
                proposals_by_status: statusCounts,
                acceptance_rate: decided > 0 ? (accepted.length / decided) * 100 : 0,
                average_value: proposals.length > 0 ? proposals.reduce((s, p) => s + (p.total_amount || 0), 0) / proposals.length : 0,
                average_time_to_accept: 0,
                ai_generated_count: proposals.filter(p => p.ai_generated).length,
                total_revenue_proposed: proposals.reduce((s, p) => s + (p.total_amount || 0), 0),
                total_revenue_accepted: accepted.reduce((s, p) => s + (p.total_amount || 0), 0),
                top_performing_templates: [],
            };
            return { success: true, data: analytics };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_ANALYTICS_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // TEMPLATES
    // --------------------------------------------------------------------------
    async listTemplates(templateType: 'proposal'): Promise<ServiceResponse<DocumentTemplate[]>> {
        try {
            const { data, error } = await supabase
                .from('document_templates')
                .select('*')
                .eq('template_type', templateType)
                .eq('is_active', true)
                .order('name');
            if (error) {
                return { success: false, error: error.message, code: 'TEMPLATES_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: (data || []) as DocumentTemplate[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TEMPLATES_FETCH_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // DUPLICATE PROPOSAL
    // --------------------------------------------------------------------------
    async duplicateProposal(proposalId: string, userId: string): Promise<ServiceResponse<Proposal>> {
        try {
            const existing = await this.getProposal(proposalId);
            if (!existing.success) {
                return { success: false, error: 'Source proposal not found' };
            }
            // Generate new number
            const { data: numberData } = await supabase.rpc('generate_proposal_number');
            const proposalNumber = (numberData as string) || `PROP-${Date.now()}`;
            const duplicateData = {
                title: `${existing.data.title} (Copy)`,
                proposal_number: proposalNumber,
                status: 'draft' as ProposalStatus,
                client_id: existing.data.client_id || null,
                opportunity_id: existing.data.opportunity_id || null,
                created_by: userId,
                team_id: existing.data.team_id || null,
                sections: JSON.stringify(existing.data.sections),
                line_items: JSON.stringify(existing.data.line_items),
                subtotal: existing.data.subtotal,
                tax_amount: existing.data.tax_amount,
                discount_amount: existing.data.discount_amount,
                total_amount: existing.data.total_amount,
                currency: existing.data.currency,
                valid_from: new Date().toISOString(),
                valid_until: new Date(Date.now() + 30 * 86400000).toISOString(),
                ai_generated: false,
                template_id: existing.data.template_id || null,
                version: 1,
                parent_proposal_id: proposalId,
            };
            const { data, error } = await supabase
                .from('proposals')
                .insert(duplicateData)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'PROPOSAL_DUPLICATE_FAILED', service: this.name };
            }
            return { success: true, data: this.mapProposal(data) };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'PROPOSAL_DUPLICATE_ERROR', service: this.name };
        }
    }
    // --------------------------------------------------------------------------
    // HELPER: Map DB row to Proposal type
    // --------------------------------------------------------------------------
    private mapProposal(row: Record<string, unknown>): Proposal {
        return {
            id: row.id as string,
            title: row.title as string,
            proposal_number: row.proposal_number as string,
            status: row.status as ProposalStatus,
            client_id: row.client_id as string | undefined,
            opportunity_id: row.opportunity_id as string | undefined,
            created_by: row.created_by as string,
            team_id: row.team_id as string | undefined,
            sections: typeof row.sections === 'string' ? JSON.parse(row.sections) : (row.sections || []),
            line_items: typeof row.line_items === 'string' ? JSON.parse(row.line_items) : (row.line_items || []),
            subtotal: Number(row.subtotal) || 0,
            tax_amount: Number(row.tax_amount) || 0,
            discount_amount: Number(row.discount_amount) || 0,
            total_amount: Number(row.total_amount) || 0,
            currency: (row.currency as string) || 'AUD',
            valid_from: row.valid_from as string,
            valid_until: row.valid_until as string,
            ai_generated: row.ai_generated as boolean,
            ai_model_used: row.ai_model_used as string | undefined,
            ai_prompt_context: row.ai_prompt_context as string | undefined,
            sent_at: row.sent_at as string | undefined,
            viewed_at: row.viewed_at as string | undefined,
            accepted_at: row.accepted_at as string | undefined,
            rejected_at: row.rejected_at as string | undefined,
            rejection_reason: row.rejection_reason as string | undefined,
            template_id: row.template_id as string | undefined,
            version: Number(row.version) || 1,
            parent_proposal_id: row.parent_proposal_id as string | undefined,
            notes: row.notes as string | undefined,
            metadata: row.metadata as Proposal['metadata'],
            created_at: row.created_at as string,
            updated_at: row.updated_at as string,
        };
    }
}
/**
 * Singleton proposal service instance
 */
export const proposalService = new ProposalService();
